import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import {
  Megaphone, Plus, PencilSimple, Trash, ArrowsClockwise, Target,
  TrendUp, CurrencyInr, ChartLine, CheckCircle, XCircle, MagnifyingGlass,
  Play, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "", code: "", platform: "meta_ads", source: "",
  utm_source: "", utm_medium: "", utm_campaign: "",
  budget: 0, currency: "INR",
  start_date: "", end_date: "",
  active: true, notes: "",
};

function pctColor(v) {
  if (v >= 30) return "text-emerald-700";
  if (v >= 15) return "text-amber-700";
  return "text-red-700";
}

function roiColor(v) {
  if (v > 0) return "text-emerald-700";
  if (v === 0) return "text-muted-foreground";
  return "text-red-700";
}

function inr(n) {
  if (!n) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
}

export default function Campaigns() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [campaigns, setCampaigns] = useState([]);
  const [efficiency, setEfficiency] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [showOnlyActive, setShowOnlyActive] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [backfilling, setBackfilling] = useState(false);
  const [backfillDry, setBackfillDry] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [list, eff, plats] = await Promise.all([
        api.get("/campaigns"),
        api.get("/campaigns/efficiency", { params }),
        api.get("/campaigns/platforms"),
      ]);
      setCampaigns(list.data);
      setEfficiency(eff.data);
      setPlatforms(plats.data.platforms);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, platform: platforms[0] || "meta_ads" });
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      code: c.code || "",
      platform: c.platform || "other",
      source: c.source || "",
      utm_source: c.utm_source || "",
      utm_medium: c.utm_medium || "",
      utm_campaign: c.utm_campaign || "",
      budget: c.budget || 0,
      currency: c.currency || "INR",
      start_date: c.start_date || "",
      end_date: c.end_date || "",
      active: c.active !== false,
      notes: c.notes || "",
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Campaign name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, budget: parseFloat(form.budget) || 0 };
      if (editing) {
        await api.patch(`/campaigns/${editing.id}`, payload);
        toast.success("Campaign updated");
      } else {
        await api.post("/campaigns", payload);
        toast.success("Campaign created");
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? Attributed leads will be detached but their utm_* history is kept.`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runBackfill = async (dry = true) => {
    setBackfilling(true);
    try {
      const { data } = await api.post(`/campaigns/backfill?dry_run=${dry}`);
      if (dry) {
        setBackfillDry(data);
        toast.info(`${data.matched} lead(s) can be attributed. Click Apply to save.`);
      } else {
        toast.success(`Attributed ${data.applied} lead(s) to campaigns`);
        setBackfillDry(null);
        load();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBackfilling(false);
  };

  const filteredCampaigns = useMemo(() => {
    let out = campaigns;
    if (showOnlyActive) out = out.filter((c) => c.active !== false);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      out = out.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(s) ||
          (c.code || "").toLowerCase().includes(s) ||
          (c.utm_campaign || "").toLowerCase().includes(s),
      );
    }
    return out;
  }, [campaigns, search, showOnlyActive]);

  // KPI map by campaign_id for quick lookup
  const effByCamp = useMemo(() => {
    const m = {};
    (efficiency?.campaigns || []).forEach((e) => { if (e.campaign_id) m[e.campaign_id] = e; });
    return m;
  }, [efficiency]);

  const summary = efficiency?.summary || {};

  return (
    <div className="p-6 space-y-6" data-testid="campaigns-page">
      <PageHeader
        eyebrow="Attribution"
        title="Campaigns"
        subtitle="Track which ad campaigns drive real revenue. Every inbound lead is auto-matched to a campaign via UTM parameters."
        icon={Megaphone}
        actions={
          isAdmin && (
            <button
              onClick={openCreate}
              data-testid="new-campaign-btn"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90"
            >
              <Plus size={14} weight="bold" /> New campaign
            </button>
          )
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Active campaigns" value={summary.total_campaigns ?? 0} icon={Target} />
        <KPI label="Total leads" value={summary.total_leads ?? 0} icon={TrendUp} />
        <KPI label="Converted" value={summary.total_converted ?? 0} sub={`${summary.overall_conversion_rate ?? 0}%`} icon={CheckCircle} />
        <KPI label="Spend" value={`₹${inr(summary.total_budget)}`} icon={CurrencyInr} />
        <KPI label="ROI" value={`${summary.overall_roi_pct ?? 0}%`} icon={ChartLine} accent={roiColor(summary.overall_roi_pct ?? 0)} />
      </div>

      {/* Filter bar */}
      <div className="border border-border bg-white p-4 flex flex-wrap items-center gap-3">
        <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="camp-date-from" className="ml-2 border border-border px-2 py-1 text-xs" />
        </label>
        <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="camp-date-to" className="ml-2 border border-border px-2 py-1 text-xs" />
        </label>
        <div className="flex items-center gap-2 border border-border px-2 py-1">
          <MagnifyingGlass size={12} className="text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="camp-search" placeholder="name / code / utm" className="text-xs outline-none w-40" />
        </div>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={showOnlyActive} onChange={(e) => setShowOnlyActive(e.target.checked)} data-testid="camp-only-active" /> Active only
        </label>
        <button onClick={load} data-testid="camp-refresh" className="ml-auto inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary">
          <ArrowsClockwise size={12} /> Refresh
        </button>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => runBackfill(true)} data-testid="camp-backfill-dry" disabled={backfilling} className="inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-40">
              <Sparkle size={12} /> {backfilling ? "Working…" : "Backfill (dry-run)"}
            </button>
            {backfillDry?.matched > 0 && (
              <button onClick={() => runBackfill(false)} data-testid="camp-backfill-apply" disabled={backfilling} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-40">
                <Play size={12} weight="fill" /> Apply {backfillDry.matched} match(es)
              </button>
            )}
          </div>
        )}
      </div>

      {/* Efficiency table */}
      <div className="border border-border bg-white overflow-x-auto">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Efficiency</div>
            <div className="text-sm font-semibold">Campaign performance</div>
          </div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">
            {loading ? "Loading…" : `${filteredCampaigns.length} campaign(s)`}
          </div>
        </div>
        <table className="w-full text-sm" data-testid="camp-efficiency-table">
          <thead className="bg-secondary/50">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <th className="px-4 py-2 font-semibold">Campaign</th>
              <th className="px-4 py-2 font-semibold">Platform</th>
              <th className="px-4 py-2 font-semibold text-right">Leads</th>
              <th className="px-4 py-2 font-semibold text-right">Qualified</th>
              <th className="px-4 py-2 font-semibold text-right">Converted</th>
              <th className="px-4 py-2 font-semibold text-right">Conv %</th>
              <th className="px-4 py-2 font-semibold text-right">Budget</th>
              <th className="px-4 py-2 font-semibold text-right">CPL</th>
              <th className="px-4 py-2 font-semibold text-right">ROI %</th>
              <th className="px-4 py-2 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {filteredCampaigns.map((c) => {
              const e = effByCamp[c.id] || { leads: 0, qualified: 0, converted: 0, conversion_rate: 0, cost_per_lead: 0, roi_pct: 0 };
              return (
                <tr key={c.id} className="border-t border-border hover:bg-secondary/30" data-testid={`camp-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">
                      {c.code || "—"} {c.utm_campaign ? ` · utm=${c.utm_campaign}` : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 border border-border text-[10px] uppercase tracking-[0.2em]">
                      {c.platform || "other"}
                    </span>
                    {c.active === false && (
                      <span className="ml-2 inline-block px-2 py-0.5 bg-muted text-[10px] uppercase tracking-[0.2em]">Paused</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{e.leads}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{e.qualified}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{e.converted}</td>
                  <td className={`px-4 py-3 text-right tabular-nums font-semibold ${pctColor(e.conversion_rate)}`}>{e.conversion_rate}%</td>
                  <td className="px-4 py-3 text-right tabular-nums">₹{inr(c.budget)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{e.cost_per_lead ? `₹${inr(e.cost_per_lead)}` : "—"}</td>
                  <td className={`px-4 py-3 text-right tabular-nums font-semibold ${roiColor(e.roi_pct)}`}>{e.roi_pct}%</td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(c)} data-testid={`camp-edit-${c.id}`} className="p-1.5 border border-border hover:bg-secondary" title="Edit">
                          <PencilSimple size={12} />
                        </button>
                        <button onClick={() => remove(c)} data-testid={`camp-delete-${c.id}`} className="p-1.5 border border-border hover:bg-red-50 text-red-600" title="Delete">
                          <Trash size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {filteredCampaigns.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {loading ? "Loading campaigns…" : "No campaigns yet. Create one to start tracking ROI."}
                </td>
              </tr>
            )}
            {/* Unattributed row */}
            {efficiency?.campaigns?.filter((e) => !e.campaign_id).map((e, i) => (
              <tr key={`unattributed-${i}`} className="border-t border-border bg-secondary/20 italic">
                <td className="px-4 py-3">
                  <div className="font-semibold">(Unattributed)</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">Leads without UTM/campaign data</div>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">—</td>
                <td className="px-4 py-3 text-right tabular-nums">{e.leads}</td>
                <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{e.qualified}</td>
                <td className="px-4 py-3 text-right tabular-nums">{e.converted}</td>
                <td className={`px-4 py-3 text-right tabular-nums ${pctColor(e.conversion_rate)}`}>{e.conversion_rate}%</td>
                <td className="px-4 py-3 text-right text-muted-foreground text-xs">—</td>
                <td className="px-4 py-3 text-right text-muted-foreground text-xs">—</td>
                <td className="px-4 py-3 text-right text-muted-foreground text-xs">—</td>
                <td className="px-4 py-3" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="camp-modal">
          <div className="bg-white border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{editing ? "Edit" : "New"} campaign</div>
                <div className="text-lg font-bold">{form.name || "Untitled"}</div>
              </div>
              <button onClick={() => setShowModal(false)} data-testid="camp-modal-close" className="p-1.5 hover:bg-secondary">
                <XCircle size={18} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <Field label="Name *" span={2}>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="camp-name" className="w-full border border-border px-3 py-2 text-sm" />
              </Field>
              <Field label="Code (unique, e.g. DIWALI26_HT)">
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="camp-code" className="w-full border border-border px-3 py-2 text-sm" />
              </Field>
              <Field label="Platform">
                <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="camp-platform" className="w-full border border-border px-3 py-2 text-sm">
                  {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="UTM source (e.g. meta, google)">
                <input value={form.utm_source} onChange={(e) => setForm({ ...form, utm_source: e.target.value })} data-testid="camp-utm-source" className="w-full border border-border px-3 py-2 text-sm" />
              </Field>
              <Field label="UTM medium (e.g. cpc, paid_social)">
                <input value={form.utm_medium} onChange={(e) => setForm({ ...form, utm_medium: e.target.value })} data-testid="camp-utm-medium" className="w-full border border-border px-3 py-2 text-sm" />
              </Field>
              <Field label="UTM campaign (attribution key)" span={2}>
                <input value={form.utm_campaign} onChange={(e) => setForm({ ...form, utm_campaign: e.target.value })} data-testid="camp-utm-campaign" placeholder="diwali_2026_ht" className="w-full border border-border px-3 py-2 text-sm" />
                <div className="text-[10px] text-muted-foreground mt-1">Inbound leads whose <code>utm_campaign</code> matches this value are auto-attributed here.</div>
              </Field>
              <Field label="Budget">
                <div className="flex items-center gap-2">
                  <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="camp-currency" className="w-16 border border-border px-2 py-2 text-sm" />
                  <input type="number" step="1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="camp-budget" className="flex-1 border border-border px-3 py-2 text-sm" />
                </div>
              </Field>
              <Field label="Status">
                <label className="flex items-center gap-2 text-sm py-2">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="camp-active" />
                  <span>{form.active ? "Active — auto-attributes new leads" : "Paused — new leads won't be attributed"}</span>
                </label>
              </Field>
              <Field label="Start date">
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="camp-start" className="w-full border border-border px-3 py-2 text-sm" />
              </Field>
              <Field label="End date">
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="camp-end" className="w-full border border-border px-3 py-2 text-sm" />
              </Field>
              <Field label="Notes" span={2}>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="camp-notes" rows={2} className="w-full border border-border px-3 py-2 text-sm" />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2 bg-secondary/40">
              <button onClick={() => setShowModal(false)} data-testid="camp-cancel" className="border border-border px-4 py-2 text-xs font-semibold hover:bg-white">
                Cancel
              </button>
              <button onClick={save} disabled={saving} data-testid="camp-save" className="bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:bg-primary/90 disabled:opacity-40">
                {saving ? "Saving…" : editing ? "Save changes" : "Create campaign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="border border-border bg-white p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${accent || ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}
